import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { BrandKitPicker } from '@/components/ui/brand-kit-picker';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useBrandKits } from '@/hooks/useBrandKits';
import { WORKSPACE_URL_PARAM } from '@/lib/workspace/workspaceUrl';

/**
 * What happens when a `?w=` link names a workspace this account does not have.
 *
 * 🔴 The alternative — quietly ignoring the parameter and staying in whatever
 * workspace the stored pointer says — is the failure this component exists to
 * prevent. The URL would claim one brand while the sidebar showed another, and
 * the person would act on whichever they happened to read. That is the same
 * class of defect as the account-wide `is_default` switch: two sources of truth
 * for one piece of state, disagreeing silently.
 *
 * So an unresolvable link asks. It is the one moment where an interruption is
 * cheaper than a guess.
 *
 * Reuses `BrandKitPicker` rather than listing kits again — the switcher, the
 * composer chip and Settings → Brand all render kits through shared primitives,
 * and a fourth hand-rolled list would drift from them the first time swatches
 * or naming changed.
 */
interface WorkspaceLinkFallbackProps {
  /**
   * Passed in rather than read from `useWorkspaceUrlSync()` here on purpose:
   * that hook OWNS the activation effect, so mounting a second instance would
   * mean two effects racing to activate the same requested workspace. It is
   * called once, in AppLayout, and its verdict is handed down.
   */
  isUnresolvable: boolean;
}

export function WorkspaceLinkFallback({ isUnresolvable }: WorkspaceLinkFallbackProps) {
  const { selectableKits: kits, activeKit, setActiveKit } = useBrandKits();
  const navigate = useNavigate();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(false);

  const open = isUnresolvable && !dismissed;

  function clearParam() {
    const params = new URLSearchParams(location.search);
    params.delete(WORKSPACE_URL_PARAM);
    const qs = params.toString();
    navigate(`${location.pathname}${qs ? `?${qs}` : ''}`, { replace: true });
  }

  function stay() {
    setDismissed(true);
    clearParam();
  }

  function choose(kitId: string | null) {
    if (kitId) setActiveKit(kitId);
    setDismissed(true);
    clearParam();
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && stay()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>That workspace isn&apos;t available</DialogTitle>
          <DialogDescription>
            This link points to a workspace that isn&apos;t on your account, or that has been
            archived. Choose one of yours to continue.
          </DialogDescription>
        </DialogHeader>

        <BrandKitPicker
          value={activeKit?.id ?? null}
          onChange={(kitId) => choose(kitId)}
          kits={kits}
          showDefaultOption={false}
          placeholder="Choose a workspace"
        />

        <DialogFooter>
          <Button variant="neutral" onClick={stay}>
            Stay in {activeKit?.name ?? 'your current workspace'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
